import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '$lib/components/ui/Button';
import { Input } from '$lib/components/ui/Input';
import { SavedSearchSummaryLine } from './SavedSearchSummaryLine';
import type { UnitsQuery } from '$lib/api/types';
import { createSavedSearch, type SavedSearchSummary } from '$lib/lib/savedSearches';
import { __test } from '$lib/lib/savedSearches';

const { summarize } = __test;

export interface SaveSearchDialogProps {
  open: boolean;
  query: UnitsQuery;
  search: string;
  onClose: () => void;
  onSaved?: (id: string) => void;
}

export function SaveSearchDialog({ open, query, search, onClose, onSaved }: SaveSearchDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // Reset state on open and steal focus.
  useEffect(() => {
    if (!open) return;
    setName(suggestName(query));
    setError(null);
    const timer = window.setTimeout(() => inputRef.current?.select(), 0);
    return () => window.clearTimeout(timer);
  }, [open, query]);

  // Esc closes the dialog.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const summary: SavedSearchSummary = summarize(query);

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('saveSearch.errorEmpty'));
      return;
    }
    const item = createSavedSearch({ name: trimmed, search, query });
    onSaved?.(item.id);
    onClose();
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('saveSearch.dialogTitle')}
      data-testid="save-search-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/35 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded border border-rule bg-surface p-5 shadow-elev-3"
      >
        <header className="flex items-center gap-2">
          <Bookmark size={14} className="text-accent" />
          <h2 className="text-sm font-semibold text-fg">{t('saveSearch.headerTitle')}</h2>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            className="ml-auto grid h-6 w-6 place-items-center rounded-xs text-fg-subtle hover:bg-surface-2"
          >
            <X size={14} />
          </button>
        </header>
        <p className="mt-2 text-2xs text-fg-muted">
          {t('saveSearch.subtitle')}
        </p>

        <label htmlFor="save-search-name" className="mt-4 block text-xs text-fg-muted">
          {t('saveSearch.name')}
        </label>
        <Input
          id="save-search-name"
          ref={inputRef}
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          className="mt-1"
          placeholder={t('saveSearch.namePlaceholder')}
        />
        {error && (
          <p role="alert" className="mt-1 text-2xs text-danger">
            {error}
          </p>
        )}

        <div className="mt-3 rounded-sm border border-rule bg-surface-2 p-2 text-xs">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle">{t('saveSearch.filtersCaptured')}</div>
          <div className="mt-1 break-words">
            <SavedSearchSummaryLine summary={summary} />
          </div>
        </div>

        <footer className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={!name.trim()}>
            {t('common.save')}
          </Button>
        </footer>
      </form>
    </div>,
    document.body
  );
}

function suggestName(query: UnitsQuery): string {
  const parts: string[] = [];
  if (query.q) parts.push(`"${query.q}"`);
  if (query.postnummer?.length) parts.push(query.postnummer.slice(0, 3).join(', '));
  if (query.m2Min != null || query.m2Max != null) parts.push(`${query.m2Min ?? ''}–${query.m2Max ?? ''} m²`);
  if (query.roomsMin != null || query.roomsMax != null) parts.push(`${query.roomsMin ?? ''}–${query.roomsMax ?? ''} rooms`);
  if (query.priceMin != null || query.priceMax != null) parts.push(`${query.priceMin ?? ''}–${query.priceMax ?? ''} kr`);
  if (parts.length === 0) return 'All units';
  return parts.join(' · ');
}
